import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useState } from 'react';
import { faHotel, faLocationDot, faTimes } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { OverlayTrigger } from 'ui';
import Field from './Field.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getGroupedPlaces } from '../../../utils/bookerUtils.js';
import { BookerContext } from '../Booker.js';
import { useFormContext } from 'react-hook-form';
const AutoCompleteSelector = ({ hotelList, selection, onSelect, myErrors, vertical }) => {
    const intl = useIntl();
    const { useTailwindPrefix, version } = useContext(BookerContext);
    const { setValue } = useFormContext();
    const originalOptions = getGroupedPlaces(hotelList);
    const [options, setOptions] = useState(originalOptions);
    const [isOpen, setIsOpen] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');
    const hasMultipleDestinations = originalOptions.length > 1;
    const hasManyResults = originalOptions.some((o) => o.hotels.length > 5);
    useEffect(() => {
        if (selection) {
            drawSelection();
        }
    }, [selection]);
    function onToggle(data) {
        setIsOpen(data);
        if (data) {
            if (document && document.body) {
                let orig = document.body.className;
                if (!useTailwindPrefix) {
                    if (orig.indexOf('fixed md:relative') < 0) {
                        document.body.className = orig + (orig ? ' ' : '') + 'fixed md:relative';
                    }
                }
                else {
                    if (orig.indexOf('tw-fixed md:tw-relative') < 0) {
                        document.body.className = orig + (orig ? ' ' : '') + 'tw-fixed md:tw-relative';
                    }
                }
            }
        }
        else {
            if (document && document.body) {
                if (!useTailwindPrefix) {
                    document.body.className = document.body.className.replace(/ ?fixed md:relative/, '');
                }
                else {
                    document.body.className = document.body.className.replace(/ ?tw-fixed md:tw-relative/, '');
                }
            }
        }
    }
    const onInputChange = (e) => {
        const text = e.target.value;
        if (selection && text !== drawSelection()) {
            setValue('destination', undefined);
        }
        let result = [];
        if (text !== '' && text.length > 2) {
            const regex = new RegExp(text, 'i');
            result = hotelList.filter((h) => regex.test(h.name) || regex.test(h.destination.name));
        }
        else {
            result = hotelList;
        }
        setSearchInputValue(text);
        setOptions(getGroupedPlaces(result));
        onToggle(true);
    };
    function drawSelection() {
        var _a;
        let result = null;
        if (selection && hotelList.length > 0) {
            if (selection.destinationType === 'hotel') {
                result = hotelList.find((h) => h.id === selection.id);
            }
            else {
                result = (_a = options.find((o) => o.destination.id === selection.id)) === null || _a === void 0 ? void 0 : _a.destination;
            }
        }
        setSearchInputValue(result ? result.name : 'No encontrado');
        return result ? result.name : null;
    }
    const bodyContainerClassName = classNames('flex', {
        'flex-wrap': hasMultipleDestinations && hasManyResults,
        'flex-col': !(hasMultipleDestinations && hasManyResults),
    });
    function onSelectEvent(data) {
        closeSelector();
        onSelect(data);
    }
    function closeSelector() {
        onToggle(false);
        setSearchInputValue('');
        setOptions(originalOptions);
        setValue('destination', undefined);
    }
    const renderOptions = (option) => {
        let result = [];
        const onDestinationSelection = () => {
            onSelectEvent({
                id: option.destination.id,
                destinationType: 'destination',
                airportCode: option.destination.airportCode,
            });
        };
        const classes = classNames('overflow-hidden', {
            'bg-gray-100': (selection === null || selection === void 0 ? void 0 : selection.id) === option.destination.id && selection.destinationType === 'destination',
            'basis-full': hasManyResults,
        });
        result.push(_jsx("div", Object.assign({ className: classes }, { children: _jsxs("div", Object.assign({ className: "flex justify-between items-center py-3 px-5 whitespace-nowrap overflow-hidden text-ellipsis bg-gray-200 hover:bg-gray-100 cursor-pointer", onClick: onDestinationSelection }, { children: [option.destination.name, _jsx("span", Object.assign({ className: "text-sm text-link-blue" }, { children: intl.formatMessage({ id: 'booker.select.destination' }) }))] })) }), `destination-${option.destination.id}`));
        option.hotels.forEach((hotel) => {
            var _a;
            const clickHotel = () => {
                onSelectEvent({
                    id: hotel.id,
                    destinationType: 'hotel',
                    airportCode: hotel.destination.airportCode,
                });
            };
            const classes = classNames('overflow-hidden', {
                'bg-gray-100': (selection === null || selection === void 0 ? void 0 : selection.id) === hotel.id && selection.destinationType === 'hotel',
                'basis-full md:basis-1/2 lg:basis-1/3': hasManyResults && hasMultipleDestinations,
            });
            result.push(_jsx("div", Object.assign({ className: classes }, { children: _jsxs("div", Object.assign({ className: "block py-3 px-5 whitespace-nowrap overflow-hidden text-ellipsis hover:bg-gray-100 cursor-pointer", onClick: clickHotel }, { children: [hotel.name, ((_a = hotel.destination) === null || _a === void 0 ? void 0 : _a.name) && (_jsx("span", Object.assign({ className: "block text-base text-gray-400" }, { children: hotel.destination.name })))] })) }), `hotel-${hotel.id}`));
        });
        return result;
    };
    return (_jsxs(OverlayTrigger, Object.assign({ isOpen: isOpen, onToggle: onToggle }, { children: [_jsx(OverlayTrigger.Trigger, Object.assign({ componentTag: "div" }, { children: _jsxs(Field, Object.assign({ vertical: vertical }, (myErrors && { hasError: true }), { children: [_jsx("div", Object.assign({ className: "w-4 flex justify-center" }, { children: _jsx(FontAwesomeIcon, { icon: hasMultipleDestinations ? faLocationDot : faHotel }) })), _jsx("div", Object.assign({ className: "whitespace-nowrap w-full overflow-hidden text-ellipsis" }, { children: _jsx("input", { type: "search", value: searchInputValue, className: classNames('outline-0 w-full text-ellipsis', {
                                    'bg-transparent md:placeholder:text-widget-pill-a11y/90': version === 'padded' && !vertical && !useTailwindPrefix,
                                    'tw-bg-transparent md:placeholder:tw-text-widget-pill-a11y/90': version === 'padded' && !vertical && useTailwindPrefix,
                                }), onChange: onInputChange, placeholder: intl.formatMessage({ id: 'booker.hotel' }), autoComplete: "off" }) })), myErrors && (_jsx("div", Object.assign({ className: "text-red-400 mt-16 pl-4   overflow-hidden text-ellipsis absolute text-sm" }, { children: (myErrors === null || myErrors === void 0 ? void 0 : myErrors.type) === 'required'
                                ? intl.formatMessage({
                                    id: `booker.errors.selectHotel${hasMultipleDestinations ? '.destination' : ''}`,
                                })
                                : intl.formatMessage({ id: 'booker.errors.correctFields' }) })))] })) })), _jsxs(OverlayTrigger.Overlay, Object.assign({ className: "bg-white md:border border-gray-200 z-[1080] fixed inset-0 md:inset-auto md:shadow-lg md:absolute md:mt-0.5 md:min-w-[280px]" }, { children: [_jsxs("div", Object.assign({ className: "md:hidden flex justify-between items-center px-4 border-b border-gray-200 h-[50px]" }, { children: [_jsx("div", Object.assign({ className: "text-3xl" }, { children: intl.formatMessage({ id: 'booker.hotel' }) })), _jsx("button", Object.assign({ onClick: closeSelector, className: "cursor-pointer text-2xl" }, { children: _jsx(FontAwesomeIcon, { icon: faTimes }) }))] })), _jsx("div", Object.assign({ className: "p-4 md:hidden" }, { children: _jsxs("div", Object.assign({ className: "border rounded w-full px-2 flex items-center space-x-2" }, { children: [_jsx("input", { type: "text", value: searchInputValue, className: " w-full py-2 outline-none text-ellipsis", onChange: onInputChange }), (searchInputValue === null || searchInputValue === void 0 ? void 0 : searchInputValue.length) > 0 && (_jsx(FontAwesomeIcon, { icon: faTimes, onClick: closeSelector, color: "#82888a" }))] })) })), searchInputValue && searchInputValue.length > 2 ? (options.length < 1 ? (_jsx("div", Object.assign({ className: "md:p-4 text-center" }, { children: intl.formatMessage({ id: 'booker.no.results' }) }))) : (_jsx("div", Object.assign({ className: bodyContainerClassName }, { children: options.map(renderOptions) })))) : (_jsx("div", Object.assign({ className: "md:p-4 text-center" }, { children: intl.formatMessage({ id: 'booker.no.results.length' }) })))] }))] })));
};
export default AutoCompleteSelector;
